import { FC, useMemo, useState } from 'react';
import { Box, Typography, useTheme, Grid, Card, CardContent } from '@mui/material';
import { useTranslation } from 'src/hooks/useTranslationHook';
import { useResponsive } from 'src/hooks/useResponsiveHook';
import { useDashboardEventsHook } from '../hooks/useDashboardEventsHook';
import {
  ActionItems,
  SummaryCards,
  CalendarWidget,
  TimeRangePicker,
  SummaryCardData,
  UpcomingItems,
} from './shared';
import {
  IEventsActionItem,
  ICalendarEvent,
  ITimeRangeParams,
  IEventsUpcomingItem,
} from '../../../../apis/dashboard/dashboard.interfaces';
import {
  Event,
  CheckCircle,
  Pending,
  Schedule,
} from '@mui/icons-material';
import { getDashboardNavigationPaths } from '../../../../apis/dashboard/dashboard.helpers';
import { useNavigate } from 'react-router-dom';

const DashboardEventsTabView: FC = () => {
  const { translateText } = useTranslation();
  const theme = useTheme();
  const { isMobile } = useResponsive();
  const [timeRange, setTimeRange] = useState<ITimeRangeParams>({
    period: 'monthly',
  });

  // Use hook directly with time range params - React Query will refetch automatically
  const { data: eventsData, isLoading } = useDashboardEventsHook(timeRange);

  const navigationPaths = getDashboardNavigationPaths();
  const navigate = useNavigate();

  // Prepare action items
  const actionItems = useMemo<IEventsActionItem[]>(() => {
    if (!eventsData?.actionItems) {
      return [];
    }
    return [
      ...eventsData.actionItems.todayEvents.items,
      ...eventsData.actionItems.todos.items,
    ];
  }, [eventsData]);

  // Prepare calendar events
  const calendarEvents = useMemo<ICalendarEvent[]>(() => {
    if (!eventsData?.calendarEvents || eventsData.calendarEvents.length === 0) {
      return [];
    }
    return eventsData.calendarEvents;
  }, [eventsData]);

  // Prepare upcoming items (deadlines) - convert to IEventsUpcomingItem format
  const upcomingDeadlines = useMemo<IEventsUpcomingItem[]>(() => {
    if (!eventsData?.upcomingItems?.deadlines || eventsData.upcomingItems.deadlines.length === 0) {
      return [];
    }
    return eventsData.upcomingItems.deadlines.map((deadline) => ({
      id: deadline.id,
      title: deadline.title,
      startDate: deadline.registrationDeadline || deadline.submissionDeadline || deadline.rsvpDeadline || deadline.startDate || '',
      endDate: deadline.endDate,
      startTime: deadline.startTime,
      endTime: deadline.endTime,
      location: deadline.location,
      description: deadline.description,
      type: deadline.type || 'other',
      registrationDeadline: deadline.registrationDeadline,
      submissionDeadline: deadline.submissionDeadline,
      rsvpDeadline: deadline.rsvpDeadline,
      navigationPath: deadline.navigationPath,
    }));
  }, [eventsData]);

  // Prepare summary cards
  const summaryCards = useMemo<SummaryCardData[]>(() => {
    const cards: SummaryCardData[] = [];

    if (eventsData?.summary) {
      cards.push({
        id: 'total-events',
        title: translateText('dashboard.events.summary.totalEvents'),
        value: eventsData.summary.totalEvents || 0,
        icon: <Event />,
        color: 'primary',
        navigationPath: navigationPaths.events.list,
        isCritical: true, // Always show on mobile
      });

      cards.push({
        id: 'upcoming-events',
        title: translateText('dashboard.events.summary.upcomingEvents'),
        value: eventsData.upcomingItems?.events?.length || 0,
        icon: <Schedule />,
        color: 'info',
        navigationPath: navigationPaths.events.list,
        isCritical: true, // Always show on mobile
      });

      cards.push({
        id: 'completed-events',
        title: translateText('dashboard.events.summary.completedEvents'),
        value: eventsData.summary.completed || 0,
        icon: <CheckCircle />,
        color: 'success',
        navigationPath: navigationPaths.events.list,
      });

      if (eventsData.summary.cancelled > 0) {
        cards.push({
          id: 'cancelled-events',
          title: translateText('dashboard.events.summary.cancelledEvents'),
          value: eventsData.summary.cancelled,
          icon: <Pending />,
          color: 'error',
          navigationPath: navigationPaths.events.list,
        });
      }
    }

    return cards;
  }, [eventsData, translateText, navigationPaths]);

  const handleTimeRangeChange = (newRange: ITimeRangeParams) => {
    setTimeRange(newRange);
    // React Query will automatically refetch when timeRange changes (query key includes params)
  };

  const handleEventClick = (event: ICalendarEvent) => {
    if (event.navigationPath) {
      navigate(event.navigationPath);
    }
  };

  if (isLoading) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        minHeight={400}
        data-testid="dashboard-events-tab-loading"
      >
        <Typography variant="body2" color="textSecondary">
          {translateText('common.loading')}
        </Typography>
      </Box>
    );
  }

  return (
    <Box
      sx={{
        pb: { xs: 2, sm: 4 },
        px: { xs: 1, sm: 2 },
      }}
      data-testid="dashboard-events-tab-container"
    >
      {/* Row 1: Summary Section - Hero Section */}
      {summaryCards.length > 0 && (
        <Box mb={4}>
          <Box
            display="flex"
            alignItems="center"
            justifyContent="space-between"
            mb={2}
            flexWrap="wrap"
            gap={2}
          >
            <Typography variant="h6" fontWeight={600}>
              {translateText('dashboard.events.summary.title')}
            </Typography>
            <TimeRangePicker
              value={timeRange}
              onChange={handleTimeRangeChange}
              dataTestId="dashboard-events-time-range"
              showCustomRange={true}
            />
          </Box>
          <SummaryCards
            cards={summaryCards}
            columns={{ xs: 12, sm: 6, md: 3 }}
            dataTestId="dashboard-events-summary-cards"
            maxMobileCards={2}
          />
        </Box>
      )}

      {/* Row 2: Action Items and Recent Activity - Side by Side (Non-Mobile) */}
      {!isMobile ? (
        <Grid container spacing={3} mb={4}>
          {/* Action Items Column */}
          <Grid item xs={12} md={6}>
            {actionItems.length > 0 ? (
              <ActionItems
                title={translateText('dashboard.events.actionItems.title')}
                items={actionItems}
                isLoading={isLoading}
                emptyMessage={translateText('dashboard.events.actionItems.empty')}
                dataTestId="dashboard-events-action-items"
              />
            ) : (
              <Card
                sx={{
                  borderRadius: 2,
                  border: `1px solid ${theme.palette.divider}`,
                }}
              >
                <CardContent>
                  <Typography variant="body2" color="textSecondary" textAlign="center">
                    {translateText('dashboard.events.actionItems.empty')}
                  </Typography>
                </CardContent>
              </Card>
            )}
          </Grid>

          {/* Recent Activity Column */}
          <Grid item xs={12} md={6}>
            {eventsData?.recentActivity && eventsData.recentActivity.length > 0 ? (
              <Card
                sx={{
                  borderRadius: 2,
                  border: `1px solid ${theme.palette.divider}`,
                  height: '100%',
                }}
                data-testid="dashboard-events-recent-activity"
              >
                <CardContent sx={{ p: { xs: 1.5, sm: 2 } }}>
                  <Typography variant="h6" fontWeight={600} mb={2}>
                    {translateText('dashboard.events.recentActivity.title')}
                  </Typography>
                  <Box
                    sx={{
                      maxHeight: 400,
                      overflowY: 'auto',
                    }}
                  >
                    {eventsData.recentActivity.map((activity) => (
                      <Box
                        key={activity.id}
                        onClick={() => {
                          if (activity.navigationPath) {
                            navigate(activity.navigationPath);
                          }
                        }}
                        sx={{
                          display: 'flex',
                          justifyContent: 'space-between',
                          alignItems: 'center',
                          py: 1,
                          px: 1,
                          borderBottom: `1px solid ${theme.palette.divider}`,
                          cursor: activity.navigationPath ? 'pointer' : 'default',
                          '&:hover': activity.navigationPath
                            ? {
                              backgroundColor: theme.palette.action.hover,
                              borderRadius: 1,
                            }
                            : {},
                          '&:last-child': {
                            borderBottom: 'none',
                          },
                        }}
                        data-testid={`dashboard-events-recent-activity-${activity.id}`}
                      >
                        <Box sx={{ flex: 1 }}>
                          <Typography variant="body2">{activity.description}</Typography>
                          {activity.eventName && (
                            <Typography variant="caption" color="textSecondary" sx={{ display: 'block', mt: 0.5 }}>
                              {activity.eventName}
                            </Typography>
                          )}
                        </Box>
                        <Typography variant="caption" color="textSecondary" sx={{ ml: 2 }}>
                          {new Date(activity.timestamp).toLocaleDateString()}
                        </Typography>
                      </Box>
                    ))}
                  </Box>
                </CardContent>
              </Card>
            ) : (
              <Card
                sx={{
                  borderRadius: 2,
                  border: `1px solid ${theme.palette.divider}`,
                }}
              >
                <CardContent>
                  <Typography variant="body2" color="textSecondary" textAlign="center">
                    {translateText('dashboard.events.recentActivity.empty')}
                  </Typography>
                </CardContent>
              </Card>
            )}
          </Grid>
        </Grid>
      ) : (
        /* Mobile View - Row 2 Stacked */
        <>
          {/* Action Items Section - Mobile */}
          {actionItems.length > 0 && (
            <Box mb={3}>
              <ActionItems
                title={translateText('dashboard.events.actionItems.title')}
                items={actionItems}
                isLoading={isLoading}
                emptyMessage={translateText('dashboard.events.actionItems.empty')}
                dataTestId="dashboard-events-action-items"
              />
            </Box>
          )}
        </>
      )}

      {/* Row 3: Upcoming Deadlines and Calendar - Side by Side (Non-Mobile) */}
      {!isMobile ? (
        <Grid container spacing={3} mb={3}>
          {/* Upcoming Deadlines Column */}
          <Grid item xs={12} md={4}>
            {upcomingDeadlines.length > 0 ? (
              <UpcomingItems
                title={translateText('dashboard.events.upcomingItems.title')}
                items={upcomingDeadlines}
                isLoading={isLoading}
                emptyMessage={translateText('dashboard.events.upcomingItems.empty')}
                dataTestId="dashboard-events-upcoming-deadlines"
                maxItems={5}
              />
            ) : (
              <Card
                sx={{
                  borderRadius: 2,
                  border: `1px solid ${theme.palette.divider}`,
                }}
              >
                <CardContent>
                  <Typography variant="body2" color="textSecondary" textAlign="center">
                    {translateText('dashboard.events.upcomingItems.empty')}
                  </Typography>
                </CardContent>
              </Card>
            )}
          </Grid>

          {/* Calendar Widget Column */}
          <Grid item xs={12} md={8}>
            <CalendarWidget
              events={calendarEvents}
              onEventClick={handleEventClick}
              dataTestId="dashboard-events-calendar"
              showFilters={true}
            />
          </Grid>
        </Grid>
      ) : (
        /* Mobile View - Row 3 Stacked */
        <>
          {/* Upcoming Deadlines Section - Mobile */}
          {upcomingDeadlines.length > 0 && (
            <Box mb={3}>
              <UpcomingItems
                title={translateText('dashboard.events.upcomingItems.title')}
                items={upcomingDeadlines}
                isLoading={isLoading}
                emptyMessage={translateText('dashboard.events.upcomingItems.empty')}
                dataTestId="dashboard-events-upcoming-deadlines"
                maxItems={5}
              />
            </Box>
          )}

          {/* Calendar Widget Section - Mobile */}
          <Box mb={3}>
            <CalendarWidget
              events={calendarEvents}
              onEventClick={handleEventClick}
              dataTestId="dashboard-events-calendar"
              showFilters={true}
            />
          </Box>
        </>
      )}
    </Box>
  );
};

export default DashboardEventsTabView;
